import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Cloud, Check, AlertTriangle, ExternalLink, Save } from "lucide-react";
import { toast } from "sonner";

export default function KMeetSettings({ workspaceId }) {
  const [cfg, setCfg] = useState(null);
  const [calendarId, setCalendarId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    api.get("/kmeet/settings", { params: { workspace_id: workspaceId } })
      .then((r) => { setCfg(r.data); setCalendarId(r.data.calendar_id || ""); })
      .catch(() => setCfg(null));
  }, [workspaceId]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/kmeet/settings", { calendar_id: calendarId },
        { params: { workspace_id: workspaceId } });
      setCfg(data);
      toast.success("Setări kMeet salvate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu s-a putut salva");
    } finally { setSaving(false); }
  };

  if (!cfg) return null;

  return (
    <section className="bg-surface border border-line/10 mt-6" data-testid="kmeet-settings">
      <div className="px-5 py-4 border-b border-line/10 flex items-center gap-2">
        <Cloud className={`w-4 h-4 ${cfg.configured ? "text-brand" : "text-fg-muted"}`} />
        <h3 className="font-bold tracking-tight">kMeet</h3>
        <span className="label-tiny ml-2">// INFOMANIAK</span>
        <span
          data-testid="kmeet-status"
          className={`ml-auto flex items-center gap-1.5 text-xs ${cfg.configured ? "text-brand" : "text-fg-muted"}`}
        >
          {cfg.configured
            ? <><Check className="w-3.5 h-3.5" /> configurat</>
            : <><AlertTriangle className="w-3.5 h-3.5" /> neconfigurat</>}
        </span>
      </div>

      <div className="p-5 space-y-4">
        <p className="text-xs text-fg-muted leading-relaxed">
          O conferință kMeet este, la Infomaniak, un eveniment de calendar — de aceea are nevoie
          de un <span className="mono">calendar_id</span>. API-ul lor public nu expune nicio
          metodă de a lista calendarele, așa că id-ul se citește o singură dată din interfața
          ksuite și se pune aici. kMeet se deschide într-un tab nou; nu are SDK de încorporare,
          spre deosebire de apelurile Jitsi din „Start call".
        </p>

        <div>
          <label htmlFor="kmeet-calendar" className="label-tiny block mb-1.5">calendar_id</label>
          <div className="flex flex-wrap items-center gap-2">
            <input
              id="kmeet-calendar" data-testid="kmeet-calendar-id"
              value={calendarId} inputMode="numeric"
              onChange={(e) => setCalendarId(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="ex. 123456"
              className="input-flat mono flex-1 min-w-[160px]"
            />
            <button
              type="button" data-testid="kmeet-save" onClick={save} disabled={saving}
              className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50"
            ><Save className="w-3.5 h-3.5" /> {saving ? "Se salvează…" : "Salvează"}</button>
          </div>
          <p className="text-[11px] text-fg-muted mt-1.5 leading-relaxed">
            Îl găsești în <a href="https://ksuite.infomaniak.com/calendar" target="_blank"
              rel="noopener noreferrer" className="text-brand-2 hover:text-brand inline-flex items-center gap-1">
              ksuite.infomaniak.com/calendar <ExternalLink className="w-3 h-3" /></a> — deschide
            calendarul dorit și citește numărul din URL. Trebuie să fie numeric și să aparțină
            contului a cărui cheie e pe server, altfel Infomaniak răspunde „not authorized".
          </p>
        </div>

        <div className="pt-3 border-t border-line/10">
          <p className="label-tiny mb-1.5">// CHEIE SERVER</p>
          <p className="text-xs flex items-center gap-1.5">
            {cfg.api_key_configured
              ? <><Check className="w-3.5 h-3.5 text-brand" /> <span className="mono">INFOMANIAK_API_KEY</span> e setată</>
              : <><AlertTriangle className="w-3.5 h-3.5 text-fg-muted" /> <span className="mono">INFOMANIAK_API_KEY</span> lipsește din environment</>}
          </p>
        </div>
      </div>
    </section>
  );
}
